import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Input, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { ErrorToaster, successToaster } from "components/common/toast";
import { mediaUrl } from "utils/mediaUrl";
import {
  ApproveSubmissionAPI, CreateProducerLinkAPI, GetProducersAPI, GetSubmissionsAPI, RejectSubmissionAPI,
} from "network/producers";

const fail = (e) => ErrorToaster(e?.message || i18n.t("producers:admin.error"));
const show = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? i18n.t("producers:admin.yes") : i18n.t("producers:admin.no")) : String(v));

const Producers = () => {
  const { t } = useTranslation("producers");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [minWines, setMinWines] = useState(3);
  const [page, setPage] = useState(1);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(false);
  const [fresh, setFresh] = useState({ name: "", email: "" });

  const load = useCallback(async () => {
    try {
      const res = await GetProducersAPI({ query, min_wines: minWines, page });
      setRows(res.data.results);
      setTotal(res.data.total);
    } catch (e) { fail(e); }
  }, [query, minWines, page]);
  useEffect(() => { load(); }, [load]);

  const makeLink = async (name, email, create = false) => {
    setBusy(name);
    try {
      const res = await CreateProducerLinkAPI({ name, email, create });
      setLink({ name, url: res.data.url });
      await load();
      return true;
    } catch (e) { fail(e); }
    setBusy(null);
    return false;
  };
  const addWinery = async () => {
    const name = fresh.name.trim();
    if (!name) return;
    if (await makeLink(name, fresh.email.trim(), true)) {
      setFresh({ name: "", email: "" });
      setAdding(false);
      setBusy(null);
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); successToaster(t("admin.linkCopied")); } catch (e) { ErrorToaster(t("admin.copyFailed")); }
  };

  return (
    <>
      {link && (
        <Card className="p-3 mb-3">
          <b>{t("admin.linkFor", { name: link.name })}</b> — {t("admin.linkNote")}
          <Input readOnly value={link.url} className="my-2" onFocus={(e) => e.target.select()} />
          <div><Button color="primary" onClick={copy}>{t("admin.copy")}</Button> <Button onClick={() => setLink(null)}>{t("admin.close")}</Button></div>
        </Card>
      )}
      {adding && (
        <Card className="p-3 mb-3">
          <b>{t("admin.newWinery")}</b>
          <p className="text-muted mb-2">{t("admin.newWineryNote")}</p>
          <div className="d-flex flex-wrap" style={{ gap: 10 }}>
            <Input style={{ maxWidth: 320 }} placeholder={t("admin.wineryName")} value={fresh.name} maxLength={255}
              onChange={(e) => setFresh({ ...fresh, name: e.target.value })} />
            <Input style={{ maxWidth: 280 }} type="email" placeholder={t("admin.wineryEmail")} value={fresh.email}
              onChange={(e) => setFresh({ ...fresh, email: e.target.value })} />
            <Button color="primary" disabled={!fresh.name.trim() || busy !== null} onClick={addWinery}>{t("admin.createWineryLink")}</Button>
            <Button onClick={() => setAdding(false)}>{t("admin.close")}</Button>
          </div>
        </Card>
      )}
      <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
        <Button color="primary" onClick={() => setAdding(true)}>{t("admin.addWinery")}</Button>
        <Input style={{ maxWidth: 280 }} placeholder={t("admin.searchWinery")} value={query} onChange={(e) => { setPage(1); setQuery(e.target.value); }} />
        <Input type="select" style={{ maxWidth: 200 }} value={minWines} onChange={(e) => { setPage(1); setMinWines(e.target.value); }}>
          {[1, 3, 5, 10].map((n) => <option key={n} value={n}>{t("admin.atLeast", { count: n })}</option>)}
        </Input>
      </div>
      <Table responsive>
        <thead><tr><th>{t("admin.winery")}</th><th>{t("admin.wines")}</th><th>{t("admin.status")}</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>{r.name}</td>
              <td>{r.wines}</td>
              <td>
                {!r.producer?.has_link ? t("admin.noLink")
                  : r.producer.link_expired ? t("admin.linkExpired")
                  : r.producer.terms_accepted ? t("admin.active") : t("admin.linkCreated")}
                {r.producer?.pending > 0 && ` · ${t("admin.proposals", { count: r.producer.pending })}`}
              </td>
              <td className="text-right">
                <Button size="sm" color="primary" disabled={busy === r.name}
                  onClick={() => (r.producer?.has_link && !window.confirm(t("admin.newLinkConfirm")) ? null : makeLink(r.name, r.producer?.email))}>
                  {r.producer?.has_link ? t("admin.newLink") : t("admin.createLink")}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-between">
        <span>{t("admin.wineryCount", { count: total })}</span>
        <span>
          <Button size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</Button>{" "}
          <Button size="sm" disabled={page * 50 >= total} onClick={() => setPage(page + 1)}>›</Button>
        </span>
      </div>
    </>
  );
};

const Submissions = () => {
  const { t } = useTranslation("producers");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { setItems((await GetSubmissionsAPI("pending")).data.results); } catch (e) { fail(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (item, approve) => {
    let note = "";
    if (!approve) { note = window.prompt(t("admin.rejectReason")); if (note === null) return; }
    setBusy(item.uuid);
    try {
      if (approve) await ApproveSubmissionAPI(item.uuid); else await RejectSubmissionAPI(item.uuid, note);
      successToaster(approve ? t("admin.approved") : t("admin.rejected"));
      await load();
    } catch (e) { fail(e); }
    setBusy(null);
  };

  if (!items.length) return <p className="text-muted">{t("admin.noPending")}</p>;
  return items.map((it) => (
    <Card className="p-3 mb-3" key={it.uuid}>
      <div className="d-flex justify-content-between flex-wrap" style={{ gap: 8 }}>
        <div>
          <b>{it.is_new ? t("admin.newWine", { name: it.new_name }) : `${it.wine?.name} ${it.wine?.vintage || ""}`}</b>
          <div className="text-muted">{it.producer}</div>
        </div>
        <div>
          <Button color="success" size="sm" disabled={busy === it.uuid} onClick={() => decide(it, true)}>{t("admin.approve")}</Button>{" "}
          <Button color="danger" size="sm" disabled={busy === it.uuid} onClick={() => decide(it, false)}>{t("admin.reject")}</Button>
        </div>
      </div>
      {it.changes.length > 0 && (
        <Table size="sm" className="mt-2">
          <thead><tr><th>{t("admin.field")}</th><th>{t("admin.current")}</th><th>{t("admin.proposed")}</th></tr></thead>
          <tbody>
            {it.changes.map((c) => <tr key={c.field}><td>{c.field}</td><td>{show(c.before)}</td><td>{show(c.after)}</td></tr>)}
          </tbody>
        </Table>
      )}
      {Object.keys(it.images).length > 0 && (
        <div className="d-flex flex-wrap" style={{ gap: 10 }}>
          {Object.entries(it.images).map(([slot, url]) => (
            <div key={slot}>
              <div className="text-muted" style={{ fontSize: ".8rem" }}>{slot}</div>
              <a href={mediaUrl(url)} target="_blank" rel="noreferrer"><img src={mediaUrl(url)} alt={slot} style={{ height: 110, objectFit: "contain" }} /></a>
            </div>
          ))}
        </div>
      )}
    </Card>
  ));
};

export default function ProducersAdmin() {
  const { t } = useTranslation("producers");
  const [tab, setTab] = useState("subs");
  return (
    <div className="content">
      <h2>{t("admin.title")}</h2>
      <div className="mb-3">
        <Button color={tab === "subs" ? "primary" : "secondary"} onClick={() => setTab("subs")}>{t("admin.tabSubs")}</Button>{" "}
        <Button color={tab === "list" ? "primary" : "secondary"} onClick={() => setTab("list")}>{t("admin.tabList")}</Button>
      </div>
      {tab === "subs" ? <Submissions /> : <Producers />}
    </div>
  );
}
